/**
 * The quote a message carries ("Quote reply", ENG-4137) — pure.
 *
 * One home for the two conversions every quote path needs: a message becoming the quote
 * a reply (or an edit) points at, and an edited quote becoming the PATCH field. The PATCH
 * reads an omitted `quotedMessageId` as "unchanged" and `null` as "remove", so an editor
 * that always sent its current quote would bump `updatedAt` — and paint "(Edited)" — on a
 * message whose quote it never touched.
 */

import type {
  ChannelMessage,
  ChannelQuotedMessage,
  UpdateChannelMessageInput,
} from "../_api/channel-endpoints";
import { CHANNEL_TYPE_ORDER_COMMENT, parseChannelCid } from "./channel-cid";

/**
 * Whether an edit may change the quote. Internal comments only — the one thread whose
 * messages are edited; the platform refuses a quote change anywhere else (400).
 */
export function canEditQuote(cid: string): boolean {
  return parseChannelCid(cid)?.channelTypeId === CHANNEL_TYPE_ORDER_COMMENT;
}

/** The quote card for `message`, as the composer and the editor hold it. */
export function toQuotedMessage(message: ChannelMessage): ChannelQuotedMessage {
  return {
    id: message.id,
    userCreatedId: message.userCreatedId,
    body: message.body ?? null,
  };
}

/** Whether the editor's quote differs from the one the message was posted with. */
export function isQuoteChanged(
  original: ChannelQuotedMessage | null | undefined,
  edited: ChannelQuotedMessage | null,
): boolean {
  return (original?.id ?? null) !== (edited?.id ?? null);
}

/** The PATCH field for an edited quote: absent when unchanged, `null` when removed. */
export function toQuotePatch(
  original: ChannelQuotedMessage | null | undefined,
  edited: ChannelQuotedMessage | null,
): Pick<UpdateChannelMessageInput, "quotedMessageId"> {
  return isQuoteChanged(original, edited)
    ? { quotedMessageId: edited?.id ?? null }
    : {};
}
